<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useRelayVoicePhoneStore } from 'dashboard/stores/relayVoicePhone';
import {
  getCallAccess,
  getCalls,
  getColleagues,
  reprovisionAgent,
} from 'dashboard/api/relayVoice';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import { formatDuration, formatPhone } from 'dashboard/helper/relayVoicePhone';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import CallDetailsPanel from 'dashboard/components-next/call/CallDetailsPanel.vue';
import ProductTour from 'dashboard/components-next/tour/ProductTour.vue';
import { tourSeen } from 'dashboard/components-next/tour/tourStorage';
import MissedCalls from './MissedCalls.vue';

const PER_PAGE = 25;
const { t } = useI18n();
const { accountId } = useAccount();
const { isAdmin } = useAdmin();
const store = useStore();
const agents = useMapGetter('agents/getAgents');
const phone = useRelayVoicePhoneStore();
const route = useRoute();

const access = ref(null);
// 'mine' | 'all' | 'missed' (the team's callback queue, for everyone).
const scope = ref(route.query.view === 'missed' ? 'missed' : 'mine');
const scopes = computed(() => [
  'mine',
  ...(access.value?.all_calls ? ['all'] : []),
  'missed',
]);
const calls = ref([]);
const total = ref(0);
const page = ref(1);
const loading = ref(false);
const loadError = ref('');
const selected = ref(null);
const directory = ref([]);
const tourOpen = ref(false);
const blankFilters = () => ({
  from: '',
  to: '',
  direction: '',
  outcome: '',
  number: '',
  agent: '',
});
const filters = ref(blankFilters());
const staff = ref('');
const reprovisionMessage = ref('');
const reprovisionFailed = ref(false);
const reprovisioning = ref(false);

const outcomes = ['answered', 'missed', 'no_answer', 'busy', 'failed'];
const directions = ['inbound', 'outbound', 'local'];
const hasFilters = computed(() => Object.values(filters.value).some(Boolean));
const tourSteps = computed(() => [
  ...['SCOPE', 'FILTERS', 'NOTES'].map(key => ({
    target: `calls-${key.toLowerCase()}`,
    title: t(`RELAY_VOICE.TOUR.CALLS.${key}.TITLE`),
    body: t(`RELAY_VOICE.TOUR.CALLS.${key}.BODY`),
    before: () => {
      if (scope.value === 'missed') scope.value = 'mine';
    },
  })),
  {
    target: 'calls-scope',
    title: t('RELAY_VOICE.TOUR.CALLS.RECORDING.TITLE'),
    body: t('RELAY_VOICE.TOUR.CALLS.RECORDING.BODY'),
  },
  {
    target: 'calls-missed',
    title: t('RELAY_VOICE.TOUR.CALLS.MISSED.TITLE'),
    body: t('RELAY_VOICE.TOUR.CALLS.MISSED.BODY'),
    before: () => {
      scope.value = 'missed';
    },
  },
]);
const customer = call =>
  call.contact?.name ||
  (call.direction === 'local'
    ? t('RELAY_VOICE.CALLS.INTERNAL', { extension: call.number })
    : formatPhone(call.number) || t('RELAY_VOICE.UNKNOWN_CALLER'));
const when = call =>
  call.started_at
    ? messageTimestamp(Date.parse(call.started_at) / 1000, 'MMM d, h:mm a')
    : '—';
const badge = outcome =>
  ({
    answered: 'bg-n-teal-3 text-n-teal-11',
    missed: 'bg-n-ruby-3 text-n-ruby-11',
    busy: 'bg-n-amber-3 text-n-amber-11',
    no_answer: 'bg-n-alpha-2 text-n-slate-11',
  })[outcome] || 'bg-n-alpha-2 text-n-slate-11';
const directionIcon = call => {
  if (call.outcome === 'missed') return 'i-ph-phone-x-bold';
  return call.direction === 'inbound'
    ? 'i-ph-phone-incoming-bold'
    : 'i-ph-phone-outgoing-bold';
};

let revision = 0;
const load = async () => {
  if (scope.value === 'missed') return;
  revision += 1;
  const mine = revision;
  loading.value = true;
  loadError.value = '';
  const params = { page: page.value, per_page: PER_PAGE, scope: scope.value };
  Object.entries(filters.value).forEach(([key, value]) => {
    if (value && !(key === 'agent' && scope.value !== 'all'))
      params[key] = value;
  });
  try {
    const result = await getCalls(accountId.value, params);
    if (mine !== revision) return;
    calls.value = result.data;
    total.value = result.meta?.total || 0;
  } catch (error) {
    if (mine !== revision) return;
    calls.value = [];
    total.value = 0;
    loadError.value =
      error?.response?.status === 503
        ? t('RELAY_VOICE.CALLS.NOT_CONFIGURED')
        : t('RELAY_VOICE.CALLS.LOAD_ERROR');
  } finally {
    if (mine === revision) loading.value = false;
  }
};

let filterTimer;
watch(
  filters,
  () => {
    clearTimeout(filterTimer);
    filterTimer = setTimeout(() => {
      page.value = 1;
      load();
    }, 300);
  },
  { deep: true }
);
watch(page, load);
watch(scope, () => {
  page.value = 1;
  selected.value = null;
  load();
});
watch(
  () => phone.historyRevision,
  () => load()
);

const onSaved = annotation => {
  const row = calls.value.find(call => call.id === selected.value?.id);
  if (row) row.annotation = annotation;
};
const callBack = number => phone.requestDial(number);

const reprovision = async () => {
  if (!staff.value) return;
  reprovisioning.value = true;
  reprovisionMessage.value = '';
  try {
    const result = await reprovisionAgent(accountId.value, staff.value);
    reprovisionFailed.value = false;
    reprovisionMessage.value = t('RELAY_VOICE.ADMIN.REPROVISIONED', {
      extension: result.extension,
      outbound: result.outbound_calling
        ? t('RELAY_VOICE.ADMIN.WITH_OUTBOUND')
        : '',
    });
  } catch {
    reprovisionFailed.value = true;
    reprovisionMessage.value = t('RELAY_VOICE.ADMIN.REPROVISION_ERROR');
  } finally {
    reprovisioning.value = false;
  }
};

onMounted(async () => {
  store.dispatch('agents/get');
  try {
    access.value = await getCallAccess(accountId.value);
  } catch {
    access.value = { all_calls: false, outcomes: [] };
  }
  load();
  if (access.value.all_calls) {
    getColleagues(accountId.value)
      .then(list => {
        directory.value = list;
      })
      .catch(() => {});
  }
  if (!tourSeen('relay-calls-v2')) tourOpen.value = true;
});
</script>

<template>
  <div class="flex w-full h-full overflow-hidden bg-n-surface-1">
    <div class="flex flex-col flex-1 min-w-0 h-full">
      <header class="px-6 pt-6 pb-4">
        <div class="mx-auto max-w-5xl flex flex-col gap-4">
          <div class="flex items-start justify-between gap-4">
            <div class="min-w-0">
              <h1 class="text-xl font-medium text-n-slate-12">
                {{ t('RELAY_VOICE.CALLS.TITLE') }}
              </h1>
              <p class="text-sm text-n-slate-11 max-w-2xl mt-1">
                {{ t('RELAY_VOICE.CALLS.DESCRIPTION') }}
              </p>
            </div>
            <Button
              icon="i-ph-question-bold"
              variant="ghost"
              color="slate"
              size="sm"
              :aria-label="t('RELAY_VOICE.TOUR_BUTTON')"
              @click="tourOpen = true"
            />
          </div>

          <div
            class="flex gap-1 p-1 rounded-lg bg-n-alpha-2 self-start"
            role="tablist"
            data-tour="calls-scope"
          >
            <button
              v-for="option in scopes"
              :key="option"
              type="button"
              role="tab"
              :aria-selected="scope === option"
              class="px-3 py-1.5 rounded-md text-sm font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
              :class="
                scope === option
                  ? 'bg-n-solid-1 text-n-slate-12 shadow-sm'
                  : 'text-n-slate-11 hover:text-n-slate-12'
              "
              @click="scope = option"
            >
              {{ t(`RELAY_VOICE.CALLS.SCOPES.${option.toUpperCase()}`) }}
            </button>
          </div>

          <div
            v-if="scope !== 'missed'"
            class="flex flex-wrap items-end gap-3"
            data-tour="calls-filters"
          >
            <label class="flex flex-col gap-1 text-xs text-n-slate-11">
              {{ t('RELAY_VOICE.CALLS.FROM') }}
              <input
                v-model="filters.from"
                type="date"
                class="!mb-0 rounded-lg border border-n-weak bg-n-alpha-1 px-2.5 py-1.5 text-sm text-n-slate-12"
              />
            </label>
            <label class="flex flex-col gap-1 text-xs text-n-slate-11">
              {{ t('RELAY_VOICE.CALLS.TO') }}
              <input
                v-model="filters.to"
                type="date"
                class="!mb-0 rounded-lg border border-n-weak bg-n-alpha-1 px-2.5 py-1.5 text-sm text-n-slate-12"
              />
            </label>
            <label class="flex flex-col gap-1 text-xs text-n-slate-11">
              {{ t('RELAY_VOICE.CALLS.DIRECTION') }}
              <select
                v-model="filters.direction"
                class="!mb-0 rounded-lg border border-n-weak bg-n-alpha-1 px-2.5 py-1.5 text-sm text-n-slate-12"
              >
                <option value="">{{ t('RELAY_VOICE.CALLS.ANY') }}</option>
                <option
                  v-for="option in directions"
                  :key="option"
                  :value="option"
                >
                  {{ t(`RELAY_VOICE.DIRECTIONS.${option}`) }}
                </option>
              </select>
            </label>
            <label class="flex flex-col gap-1 text-xs text-n-slate-11">
              {{ t('RELAY_VOICE.CALLS.OUTCOME') }}
              <select
                v-model="filters.outcome"
                class="!mb-0 rounded-lg border border-n-weak bg-n-alpha-1 px-2.5 py-1.5 text-sm text-n-slate-12"
              >
                <option value="">{{ t('RELAY_VOICE.CALLS.ANY') }}</option>
                <option
                  v-for="option in outcomes"
                  :key="option"
                  :value="option"
                >
                  {{ t(`RELAY_VOICE.OUTCOMES.${option}`) }}
                </option>
              </select>
            </label>
            <label class="flex flex-col gap-1 text-xs text-n-slate-11">
              {{ t('RELAY_VOICE.CALLS.NUMBER') }}
              <input
                v-model.trim="filters.number"
                type="search"
                inputmode="tel"
                :placeholder="t('RELAY_VOICE.NUMBER_PLACEHOLDER')"
                class="!mb-0 w-40 rounded-lg border border-n-weak bg-n-alpha-1 px-2.5 py-1.5 text-sm text-n-slate-12"
              />
            </label>
            <label
              v-if="scope === 'all'"
              class="flex flex-col gap-1 text-xs text-n-slate-11"
            >
              {{ t('RELAY_VOICE.CALLS.AGENT') }}
              <select
                v-model="filters.agent"
                class="!mb-0 rounded-lg border border-n-weak bg-n-alpha-1 px-2.5 py-1.5 text-sm text-n-slate-12"
              >
                <option value="">{{ t('RELAY_VOICE.CALLS.ANY') }}</option>
                <option
                  v-for="person in directory"
                  :key="person.user_id"
                  :value="person.member_uuid"
                >
                  {{ person.name }} · {{ person.extension }}
                </option>
              </select>
            </label>
            <Button
              v-if="hasFilters"
              :label="t('RELAY_VOICE.CALLS.RESET')"
              variant="link"
              color="slate"
              size="sm"
              @click="filters = blankFilters()"
            />
          </div>
        </div>
      </header>

      <main class="flex-1 overflow-y-auto px-6 pb-6">
        <div class="mx-auto max-w-5xl">
          <MissedCalls
            v-if="scope === 'missed'"
            :account-id="accountId"
            :agents="agents"
            :can-call="phone.enabled"
            @call-back="callBack"
          />
          <div
            v-else-if="loading && !calls.length"
            class="flex justify-center py-16"
          >
            <Spinner />
          </div>
          <p
            v-else-if="loadError"
            role="alert"
            class="py-16 text-center text-sm text-n-ruby-11"
          >
            {{ loadError }}
          </p>
          <p
            v-else-if="!calls.length"
            class="py-16 text-center text-sm text-n-slate-10"
          >
            {{
              hasFilters || scope === 'all'
                ? t('RELAY_VOICE.CALLS.EMPTY')
                : t('RELAY_VOICE.CALLS.EMPTY_MINE')
            }}
          </p>
          <table v-else class="w-full text-sm border-separate border-spacing-0">
            <thead>
              <tr class="text-xs text-n-slate-10 text-start">
                <th scope="col" class="font-medium text-start py-2 ps-3">
                  {{ t('RELAY_VOICE.CALLS.COLUMNS.CUSTOMER') }}
                </th>
                <th
                  v-if="scope === 'all'"
                  scope="col"
                  class="font-medium text-start py-2 hidden md:table-cell"
                >
                  {{ t('RELAY_VOICE.CALLS.COLUMNS.AGENT') }}
                </th>
                <th scope="col" class="font-medium text-start py-2">
                  {{ t('RELAY_VOICE.CALLS.COLUMNS.RESULT') }}
                </th>
                <th
                  scope="col"
                  class="font-medium text-end py-2 hidden sm:table-cell"
                >
                  {{ t('RELAY_VOICE.CALLS.COLUMNS.DURATION') }}
                </th>
                <th scope="col" class="font-medium text-end py-2 pe-3">
                  {{ t('RELAY_VOICE.CALLS.COLUMNS.WHEN') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="call in calls"
                :key="call.id"
                tabindex="0"
                class="cursor-pointer outline-none focus-visible:bg-n-alpha-2 hover:bg-n-alpha-1"
                :class="selected?.id === call.id ? 'bg-n-alpha-2' : ''"
                :aria-label="t('RELAY_VOICE.CALLS.DETAILS')"
                @click="selected = call"
                @keydown.enter="selected = call"
              >
                <td class="py-2.5 ps-3 border-t border-n-weak rounded-s-lg">
                  <div class="flex items-center gap-3 min-w-0">
                    <span
                      class="flex items-center justify-center size-8 rounded-full shrink-0"
                      :class="
                        call.outcome === 'missed'
                          ? 'bg-n-ruby-3 text-n-ruby-11'
                          : 'bg-n-alpha-2 text-n-slate-11'
                      "
                    >
                      <span
                        :class="directionIcon(call)"
                        class="size-4"
                        aria-hidden="true"
                      />
                    </span>
                    <span class="min-w-0">
                      <span class="block font-medium text-n-slate-12 truncate">
                        {{ customer(call) }}
                      </span>
                      <span
                        class="block text-xs text-n-slate-10 tabular-nums truncate"
                      >
                        {{
                          call.contact
                            ? formatPhone(call.number)
                            : t(
                                `RELAY_VOICE.DIRECTIONS.${call.direction || 'outbound'}`
                              )
                        }}
                      </span>
                    </span>
                    <span
                      v-if="call.annotation"
                      class="i-ph-note-pencil-bold size-4 text-n-slate-10 shrink-0"
                      :title="t('RELAY_VOICE.CALLS.HAS_NOTE')"
                      :aria-label="t('RELAY_VOICE.CALLS.HAS_NOTE')"
                      role="img"
                    />
                  </div>
                </td>
                <td
                  v-if="scope === 'all'"
                  class="py-2.5 border-t border-n-weak text-n-slate-11 hidden md:table-cell"
                >
                  {{ call.relay_agent?.name || call.agent?.name || '—' }}
                </td>
                <td class="py-2.5 border-t border-n-weak">
                  <span
                    class="inline-flex px-2 py-0.5 rounded-full text-xs font-medium"
                    :class="badge(call.outcome)"
                  >
                    {{ t(`RELAY_VOICE.OUTCOMES.${call.outcome}`) }}
                  </span>
                </td>
                <td
                  class="py-2.5 border-t border-n-weak text-end tabular-nums text-n-slate-11 hidden sm:table-cell"
                >
                  {{
                    call.talk_seconds ? formatDuration(call.talk_seconds) : '—'
                  }}
                </td>
                <td
                  class="py-2.5 pe-3 border-t border-n-weak text-end text-n-slate-11 whitespace-nowrap rounded-e-lg"
                >
                  {{ when(call) }}
                </td>
              </tr>
            </tbody>
          </table>

          <section
            v-if="isAdmin && scope !== 'missed'"
            class="mt-10 p-4 rounded-xl border border-n-weak flex flex-col gap-3"
          >
            <h2 class="text-sm font-semibold text-n-slate-12">
              {{ t('RELAY_VOICE.ADMIN.REPROVISION') }}
            </h2>
            <p class="text-xs text-n-slate-11">
              {{ t('RELAY_VOICE.ADMIN.REPROVISION_HINT') }}
            </p>
            <div class="flex flex-wrap items-center gap-2">
              <select
                v-model="staff"
                :aria-label="t('RELAY_VOICE.ADMIN.SELECT_STAFF')"
                class="!mb-0 rounded-lg border border-n-weak bg-n-alpha-1 px-2.5 py-1.5 text-sm text-n-slate-12"
              >
                <option value="">
                  {{ t('RELAY_VOICE.ADMIN.SELECT_STAFF') }}
                </option>
                <option
                  v-for="agent in agents"
                  :key="agent.id"
                  :value="agent.id"
                >
                  {{ agent.name }}
                </option>
              </select>
              <Button
                :label="t('RELAY_VOICE.ADMIN.REPROVISION')"
                size="sm"
                variant="faded"
                :disabled="!staff"
                :is-loading="reprovisioning"
                @click="reprovision"
              />
              <p
                v-if="reprovisionMessage"
                class="text-xs"
                :class="reprovisionFailed ? 'text-n-ruby-11' : 'text-n-teal-11'"
                :role="reprovisionFailed ? 'alert' : 'status'"
              >
                {{ reprovisionMessage }}
              </p>
            </div>
          </section>
        </div>
      </main>
      <footer
        v-if="scope !== 'missed' && total > PER_PAGE"
        class="sticky bottom-0"
      >
        <PaginationFooter
          v-model:current-page="page"
          :total-items="total"
          :items-per-page="PER_PAGE"
        />
      </footer>
    </div>
    <CallDetailsPanel
      v-if="selected && scope !== 'missed'"
      :account-id="accountId"
      :call="selected"
      :outcomes="access?.outcomes || []"
      :agents="agents"
      :can-call="phone.enabled"
      @close="selected = null"
      @saved="onSaved"
      @call-back="callBack"
    />
    <ProductTour
      v-model="tourOpen"
      :steps="tourSteps"
      storage-key="relay-calls-v2"
    />
  </div>
</template>
